import { hexToRgb } from './color';
import { fontStack } from './fonts';

// theme.colors key -> CSS variable (consumed by tailwind.config.js)
const COLOR_VARS = {
  primary: '--color-primary',
  primaryContrast: '--color-primary-contrast',
  accent: '--color-accent',
  accentContrast: '--color-accent-contrast',
  background: '--color-background',
  surface: '--color-surface',
  surfaceAlt: '--color-surface-alt',
  text: '--color-text',
  muted: '--color-muted',
  border: '--color-border',
  sale: '--color-sale',
};

// Named tokens -> concrete CSS. Themes never carry raw CSS.
export const RADIUS = { none: '0px', sm: '4px', md: '10px', lg: '18px' };
const BUTTON_RADIUS = {
  pill: '9999px',
  // Follows the theme's radius but never fully square.
  rounded: null,
  square: '0px',
};
const TRACKING = { tight: '-0.02em', normal: '0em', wide: '0.06em' };

/**
 * A resolved theme (see resolveTheme) -> { '--css-var': value }.
 * Colors are written as space-separated RGB channels so Tailwind's opacity
 * modifiers keep working (bg-primary/10).
 */
export function themeToCssVars(theme) {
  const vars = {};
  Object.entries(COLOR_VARS).forEach(([key, cssVar]) => {
    const rgb = hexToRgb(theme.colors[key]);
    if (rgb) vars[cssVar] = rgb.join(' ');
  });

  const ty = theme.typography;
  vars['--font-heading'] = fontStack(ty.heading);
  vars['--font-body'] = fontStack(ty.body);
  vars['--heading-weight'] = String(ty.headingWeightResolved || ty.headingWeight);
  vars['--heading-case'] = ty.headingCase === 'uppercase' ? 'uppercase' : 'none';
  // Uppercase needs a little air even when the theme asks for tight tracking.
  vars['--heading-tracking'] =
    ty.headingCase === 'uppercase' && ty.tracking === 'tight' ? '-0.01em' : TRACKING[ty.tracking];

  const radius = RADIUS[theme.shape.radius] || RADIUS.md;
  vars['--radius'] = radius;
  vars['--radius-btn'] =
    BUTTON_RADIUS[theme.shape.button] ?? `max(${radius}, 6px)`;
  return vars;
}

/** Write a resolved theme to CSS variables on `root` (default :root). */
export function applyTheme(theme, root = document.documentElement) {
  const vars = themeToCssVars(theme);
  Object.entries(vars).forEach(([name, value]) => root.style.setProperty(name, value));
  root.dataset.preset = theme.preset;
  if (root === document.documentElement) {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.colors.background);
  }
}
